import { 
    computeAltmanRatios, 
    computeFulmerRatios, 
    computeSpringateRatios, 
    computeTafflerRatios 
  } from './models';
  
  /**
   * Get detailed analysis for Altman Z-score
   * @param {Object} result - Result from calculateAltmanZScore
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getAltmanZAnalysis = (result, financialData) => {
    const { x1, x2, x3, x4, x5 } = result.ratios;
    const weakPoints = [];
    if (x1 < 0.1) weakPoints.push('Низкий уровень оборотного капитала (X1)');
    if (x2 < 0.1) weakPoints.push('Недостаточная нераспределенная прибыль (X2)');
    if (x3 < 0.05) weakPoints.push('Низкая рентабельность активов по EBIT (X3)');
    if (x4 < 0.5) weakPoints.push('Неоптимальное соотношение капитала и обязательств (X4)');
    if (x5 < 0.8) weakPoints.push('Недостаточная эффективность активов (X5)');
  
    const trend = {
      overall: x3 > 0.1 && x4 > 0.7 ? 'improving' : (x3 < 0.05 || x4 < 0.3) ? 'worsening' : 'stable',
      liquidity: x1 > 0.2 ? 'improving' : x1 < 0.1 ? 'worsening' : 'stable',
      profitability: x2 > 0.1 && x3 > 0.1 ? 'improving' : (x2 < 0.05 || x3 < 0.05) ? 'worsening' : 'stable',
      debt: x4 > 0.7 ? 'improving' : x4 < 0.3 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { x1, x2, x3, x4, x5 },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get detailed analysis for Fulmer model
   * @param {Object} result - Result from calculateFulmerModel
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getFulmerAnalysis = (result, financialData) => {
    const { v1, v2, v3, v4, v5, v6, v7, v8, v9 } = result.ratios;
    const weakPoints = [];
    if (v1 < 0.1) weakPoints.push('Низкая доля нераспределенной прибыли (V1)');
    if (v2 < 0.7) weakPoints.push('Недостаточный объем выручки (V2)');
    if (v3 < 0.1) weakPoints.push('Низкая прибыльность капитала (V3)');
    if (v4 < 0.2) weakPoints.push('Недостаточный рабочий капитал (V4)');
    if (v5 > 0.7) weakPoints.push('Высокая долговая нагрузка (V5)');
  
    const trend = {
      overall: v1 > 0.15 && v3 > 0.15 ? 'improving' : (v1 < 0.05 || v5 > 0.8) ? 'worsening' : 'stable',
      liquidity: v4 > 0.3 ? 'improving' : v4 < 0.1 ? 'worsening' : 'stable',
      profitability: v1 > 0.1 && v3 > 0.1 ? 'improving' : (v1 < 0.05 || v3 < 0.05) ? 'worsening' : 'stable',
      debt: v5 < 0.5 ? 'improving' : v5 > 0.7 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { v1, v2, v3, v4, v5, v6, v7, v8, v9 },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get detailed analysis for Springate model
   * @param {Object} result - Result from calculateSpringateModel
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getSpringateAnalysis = (result, financialData) => {
    const { a, b, c, d } = result.ratios;
    const weakPoints = [];
    if (a < 0.1) weakPoints.push('Недостаточный оборотный капитал (A)');
    if (b < 0.05) weakPoints.push('Низкая рентабельность активов (B)');
    if (c < 0.1) weakPoints.push('Недостаточная прибыль (C)');
    if (d < 0.7) weakPoints.push('Низкая оборачиваемость активов (D)');
  
    const trend = {
      overall: b > 0.1 && c > 0.2 ? 'improving' : (b < 0.03 || c < 0.05) ? 'worsening' : 'stable',
      liquidity: a > 0.2 ? 'improving' : a < 0.1 ? 'worsening' : 'stable',
      profitability: b > 0.08 ? 'improving' : b < 0.03 ? 'worsening' : 'stable',
      debt: c > 0.15 ? 'improving' : c < 0.05 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { a, b, c, d },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get detailed analysis for Taffler model
   * @param {Object} result - Result from calculateTafflerModel
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getTafflerAnalysis = (result, financialData) => {
    const { x1, x2, x3, x4 } = result.ratios;
    const weakPoints = [];
    if (x1 < 0.1) weakPoints.push('Низкая прибыльность (X1)');
    if (x2 < 0.5) weakPoints.push('Низкая ликвидность (X2)');
    if (x3 > 0.5) weakPoints.push('Высокая доля обязательств (X3)');
    if (x4 < 0.7) weakPoints.push('Недостаточная оборачиваемость активов (X4)');
  
    const trend = {
      overall: x1 > 0.2 && x2 > 0.7 ? 'improving' : (x1 < 0.05 || x3 > 0.6) ? 'worsening' : 'stable',
      liquidity: x2 > 0.7 ? 'improving' : x2 < 0.4 ? 'worsening' : 'stable',
      profitability: x1 > 0.15 ? 'improving' : x1 < 0.05 ? 'worsening' : 'stable',
      debt: x3 < 0.3 ? 'improving' : x3 > 0.5 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { x1, x2, x3, x4 },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get detailed analysis for Beaver model
   * @param {Object} result - Result from calculateBeaverModel
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getBeaverAnalysis = (result, financialData) => {
    const { beaverRatio, roa, debtRatio, workingCapitalRatio, currentRatio } = result.ratios;
    const weakPoints = [];
    if (beaverRatio < 0.2) weakPoints.push('Низкий коэффициент Бивера');
    if (roa < 0.04) weakPoints.push('Недостаточная рентабельность активов');
    if (debtRatio > 0.5) weakPoints.push('Высокий уровень финансового рычага');
    if (workingCapitalRatio < 0.3) weakPoints.push('Недостаточный оборотный капитал');
    if (currentRatio < 2) weakPoints.push('Низкий уровень ликвидности');
  
    const trend = {
      overall: roa > 0.06 && beaverRatio > 0.3 ? 'improving' : (roa < 0.02 || beaverRatio < 0.1) ? 'worsening' : 'stable',
      liquidity: currentRatio > 2 ? 'improving' : currentRatio < 1 ? 'worsening' : 'stable',
      profitability: roa > 0.05 ? 'improving' : roa < 0.02 ? 'worsening' : 'stable',
      debt: debtRatio < 0.4 ? 'improving' : debtRatio > 0.6 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { beaverRatio, roa, debtRatio, workingCapitalRatio, currentRatio },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get detailed analysis for Saifullin-Kadykov model
   * @param {Object} result - Result from calculateSaifullinKadykovModel
   * @param {Object} financialData - Financial data object
   * @returns {Object} Analysis with weak points and trends
   */
  export const getSaifullinKadykovAnalysis = (result, financialData) => {
    const { k1, k2, k3, k4, k5 } = result.ratios;
    const weakPoints = [];
    if (k1 < 0.1) weakPoints.push('Низкая обеспеченность собственными средствами (K1)');
    if (k2 < 2) weakPoints.push('Недостаточная текущая ликвидность (K2)');
    if (k3 < 1) weakPoints.push('Низкая оборачиваемость активов (K3)');
    if (k4 < 0.05) weakPoints.push('Недостаточная рентабельность продаж (K4)');
    if (k5 < 0.1) weakPoints.push('Низкая рентабельность капитала (K5)');
  
    const trend = {
      overall: k1 > 0.2 && k4 > 0.1 ? 'improving' : (k1 < 0.05 || k4 < 0.01) ? 'worsening' : 'stable',
      liquidity: k2 > 2 ? 'improving' : k2 < 1 ? 'worsening' : 'stable',
      profitability: k4 > 0.1 && k5 > 0.2 ? 'improving' : (k4 < 0.02 || k5 < 0.05) ? 'worsening' : 'stable',
      debt: k1 > 0.3 ? 'improving' : k1 < 0.1 ? 'worsening' : 'stable'
    };
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      components: { k1, k2, k3, k4, k5 },
      weakPoints,
      trend
    };
  };
  
  /**
   * Get comprehensive analysis from multiple models
   * @param {Object} result - Comprehensive result
   * @param {Object} modelResults - Individual model results
   * @param {Object} financialData - Financial data object
   * @returns {Object} Comprehensive analysis
   */
  export const getComprehensiveAnalysis = (result, modelResults, financialData) => {
    const allWeakPoints = [];
    const trends = { overall: [], liquidity: [], profitability: [], debt: [] };
  
    if (modelResults.altman) {
      const altmanAnalysis = getAltmanZAnalysis({ score: modelResults.altman, ratios: computeAltmanRatios(financialData) }, financialData);
      allWeakPoints.push(...altmanAnalysis.weakPoints);
      for (const key in altmanAnalysis.trend) {
        trends[key].push(altmanAnalysis.trend[key]);
      }
    }
  
    if (modelResults.fulmer) {
      const fulmerAnalysis = getFulmerAnalysis({ score: modelResults.fulmer, ratios: computeFulmerRatios(financialData) }, financialData);
      allWeakPoints.push(...fulmerAnalysis.weakPoints);
      for (const key in fulmerAnalysis.trend) {
        trends[key].push(fulmerAnalysis.trend[key]);
      }
    }
  
    if (modelResults.springate) {
      const springateAnalysis = getSpringateAnalysis({ score: modelResults.springate, ratios: computeSpringateRatios(financialData) }, financialData);
      allWeakPoints.push(...springateAnalysis.weakPoints);
      for (const key in springateAnalysis.trend) {
        trends[key].push(springateAnalysis.trend[key]);
      }
    }
  
    if (modelResults.taffler) {
      const tafflerAnalysis = getTafflerAnalysis({ score: modelResults.taffler, ratios: computeTafflerRatios(financialData) }, financialData);
      allWeakPoints.push(...tafflerAnalysis.weakPoints);
      for (const key in tafflerAnalysis.trend) {
        trends[key].push(tafflerAnalysis.trend[key]);
      }
    }
  
    if (modelResults.beaver) {
      const beaverAnalysis = getBeaverAnalysis({ score: modelResults.beaver, ratios: modelResults.beaver.ratios }, financialData);
      allWeakPoints.push(...beaverAnalysis.weakPoints);
      for (const key in beaverAnalysis.trend) {
        trends[key].push(beaverAnalysis.trend[key]);
      }
    }
  
    if (modelResults.saifullin) {
      const saifullinAnalysis = getSaifullinKadykovAnalysis({ score: modelResults.saifullin, ratios: modelResults.saifullin.ratios }, financialData);
      allWeakPoints.push(...saifullinAnalysis.weakPoints);
      for (const key in saifullinAnalysis.trend) {
        trends[key].push(saifullinAnalysis.trend[key]);
      }
    }
  
    const uniqueWeakPoints = [...new Set(allWeakPoints)];
    const finalTrends = {};
    for (const key in trends) {
      if (trends[key].length === 0) {
        finalTrends[key] = 'stable';
      } else {
        const counts = {
          improving: trends[key].filter(t => t === 'improving').length,
          worsening: trends[key].filter(t => t === 'worsening').length,
          stable: trends[key].filter(t => t === 'stable').length
        };
        finalTrends[key] = counts.improving > counts.worsening && counts.improving > counts.stable ? 'improving' :
                           counts.worsening > counts.improving && counts.worsening > counts.stable ? 'worsening' : 'stable';
      }
    }
  
    return {
      score: result.score,
      thresholds: result.thresholds,
      models: modelResults,
      weakPoints: uniqueWeakPoints.slice(0, 5),
      trend: finalTrends
    };
  };